/**
 * @description CompanyInviteModal — Headless UI Dialog for accepting company invitations
 *
 * Key features:
 * - Fetches invite details on open (GET /auth/invite-details)
 * - Shows company name, role, and masked email
 * - Handles states: loading, normal, wrong user, already accepted, expired, success, error
 * - Accept calls POST /auth/accept-company-invite, then refreshContext + switchCompany
 * - Follows SendInvoiceModal pattern (Transition.Root + Dialog + Dialog.Panel)
 *
 * @dependencies
 * - @headlessui/react: Dialog + Transition
 * - AuthContext: switchCompany, refreshContext, signOut
 * - apiClient: invite-details + accept-company-invite endpoints
 *
 * @notes
 * - Rendered on MerchantDashboard when sessionStorage has 'pending_company_invite'
 * - sessionStorage is cleared by MerchantDashboard before opening (one-time use)
 * - After accept: refreshContext() fetches updated companies list, then switchCompany()
 */

import { useState, useEffect, useRef, Fragment, useCallback } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Building2, CheckCircle, AlertTriangle, AlertCircle, Loader2, LogOut, X } from 'lucide-react';
import apiClient from '../../services/api/apiClient';
import { useAuth } from '../../contexts/AuthContext';

function CompanyInviteModal({ isOpen, onClose, inviteParams }) {
  const { switchCompany, refreshContext, signOut } = useAuth();

  // Invite details from API
  const [inviteDetails, setInviteDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Accept flow
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  // Track open transitions (only fetch on false → true)
  const prevOpenRef = useRef(false);

  // Fetch invite details when modal opens
  const fetchInviteDetails = useCallback(async () => {
    if (!inviteParams?.employeeId || !inviteParams?.companyId) return;

    setLoading(true);
    setError(null);
    setAccepted(false);

    try {
      const response = await apiClient.get('/auth/invite-details', {
        params: {
          employeeId: inviteParams.employeeId,
          companyId: inviteParams.companyId
        }
      });
      setInviteDetails(response.data);
    } catch (err) {
      const code = err.response?.data?.error?.code;
      if (code === 'INVITE_NOT_FOUND') {
        setError('This invitation could not be found. It may have been revoked.');
      } else {
        setError('Failed to load invitation details. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [inviteParams]);

  // Initialize on open transition (false → true)
  useEffect(() => {
    const justOpened = isOpen && !prevOpenRef.current;
    prevOpenRef.current = isOpen;

    if (justOpened) {
      fetchInviteDetails();
    }
  }, [isOpen, fetchInviteDetails]);

  // Auto-close after successful accept
  useEffect(() => {
    if (!accepted) return;
    const timer = setTimeout(() => {
      onClose({ accepted: true });
    }, 1500);
    return () => clearTimeout(timer);
  }, [accepted, onClose]);

  // Accept invitation handler
  const handleAccept = async () => {
    setAccepting(true);
    setError(null);

    try {
      await apiClient.post('/auth/accept-company-invite', {
        employeeId: inviteParams.employeeId,
        companyId: inviteParams.companyId
      });

      // Refresh context so new company appears in companies list
      await refreshContext();

      // Switch to the newly joined company
      await switchCompany(inviteParams.companyId);

      setAccepted(true);
    } catch (err) {
      const code = err.response?.data?.error?.code;
      const message = err.response?.data?.error?.message;

      if (code === 'INVITE_ALREADY_PROCESSED') {
        setInviteDetails(prev => prev ? { ...prev, invitationStatus: 'accepted' } : prev);
      } else if (code === 'INVITE_EMAIL_MISMATCH') {
        setError('This invitation was sent to a different email address.');
      } else if (code === 'INVITE_EXPIRED') {
        setInviteDetails(prev => prev ? { ...prev, isExpired: true } : prev);
      } else {
        setError(message || 'Failed to accept invitation. Please try again.');
      }
    } finally {
      setAccepting(false);
    }
  };

  const handleDecline = () => {
    onClose({ accepted: false });
  };

  const handleSignOut = async () => {
    await signOut();
  };

  // --- Render helpers for each state ---

  const renderLoading = () => (
    <div className="flex flex-col items-center py-10">
      <Loader2 className="h-8 w-8 animate-spin text-brand mb-4" />
      <p className="text-sm text-gray-500">Loading invitation details...</p>
    </div>
  );

  const renderError = () => (
    <div className="flex flex-col items-center py-8">
      <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mb-4">
        <AlertCircle className="w-7 h-7 text-red-600" />
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">Something went wrong</h3>
      <p className="text-sm text-gray-500 text-center mb-6 max-w-xs">{error}</p>
      <div className="flex gap-3 w-full">
        <button
          onClick={fetchInviteDetails}
          className="flex-1 rounded-xl bg-brand px-4 py-3 text-sm font-semibold text-gray-900 transition hover:bg-brand-hover focus:outline-none focus:ring-4 focus:ring-brand/30"
        >
          Try again
        </button>
        <button
          onClick={handleDecline}
          className="flex-1 rounded-xl bg-gray-100 px-4 py-3 text-sm font-semibold text-gray-700 transition hover:bg-gray-200 focus:outline-none focus:ring-4 focus:ring-gray-200"
        >
          Close
        </button>
      </div>
    </div>
  );

  const renderExpired = () => (
    <div className="flex flex-col items-center py-8">
      <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mb-4">
        <AlertCircle className="w-7 h-7 text-red-600" />
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">Invitation expired</h3>
      <p className="text-sm text-gray-500 text-center mb-6 max-w-xs">
        This invitation has expired. Please ask the company admin to resend the invitation.
      </p>
      <button
        onClick={handleDecline}
        className="w-full rounded-xl bg-gray-100 px-4 py-3 text-sm font-semibold text-gray-700 transition hover:bg-gray-200 focus:outline-none focus:ring-4 focus:ring-gray-200"
      >
        Close
      </button>
    </div>
  );

  const renderAlreadyAccepted = () => (
    <div className="flex flex-col items-center py-8">
      <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mb-4">
        <CheckCircle className="w-7 h-7 text-green-600" />
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">Already joined</h3>
      <p className="text-sm text-gray-500 text-center mb-6 max-w-xs">
        You've already accepted this invitation to {inviteDetails?.companyName}.
      </p>
      <button
        onClick={handleDecline}
        className="w-full rounded-xl bg-gray-100 px-4 py-3 text-sm font-semibold text-gray-700 transition hover:bg-gray-200 focus:outline-none focus:ring-4 focus:ring-gray-200"
      >
        Close
      </button>
    </div>
  );

  const renderWrongUser = () => (
    <div className="flex flex-col items-center py-8">
      <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mb-4">
        <AlertTriangle className="w-7 h-7 text-amber-600" />
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">Wrong account</h3>
      <p className="text-sm text-gray-500 text-center mb-2 max-w-xs">
        This invitation was sent to <span className="font-medium text-gray-700">{inviteDetails?.invitedEmail}</span>.
      </p>
      <p className="text-sm text-gray-500 text-center mb-6 max-w-xs">
        Sign out and sign in with the correct account to accept.
      </p>
      <div className="flex gap-3 w-full">
        <button
          onClick={handleSignOut}
          className="flex-1 rounded-xl bg-brand px-4 py-3 text-sm font-semibold text-gray-900 transition hover:bg-brand-hover focus:outline-none focus:ring-4 focus:ring-brand/30 flex items-center justify-center gap-2"
        >
          <LogOut className="w-4 h-4" />
          Sign out
        </button>
        <button
          onClick={handleDecline}
          className="flex-1 rounded-xl bg-gray-100 px-4 py-3 text-sm font-semibold text-gray-700 transition hover:bg-gray-200 focus:outline-none focus:ring-4 focus:ring-gray-200"
        >
          Close
        </button>
      </div>
    </div>
  );

  const renderSuccess = () => (
    <div className="flex flex-col items-center py-8">
      <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mb-4">
        <CheckCircle className="w-7 h-7 text-green-600" />
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">Joined successfully</h3>
      <p className="text-sm text-gray-500 text-center max-w-xs">
        You're now part of {inviteDetails?.companyName}. Switching to your new company...
      </p>
    </div>
  );

  const renderNormal = () => (
    <div className="flex flex-col items-center py-8">
      <div className="w-16 h-16 rounded-full bg-brand/10 flex items-center justify-center mb-5">
        <Building2 className="w-8 h-8 text-brand" />
      </div>
      <h3 className="text-xl font-bold text-gray-900 mb-1">
        {inviteDetails?.companyName}
      </h3>
      <p className="text-sm text-gray-500 mb-6">
        Role: <span className="font-medium text-gray-700">{inviteDetails?.roleName}</span>
      </p>
      <p className="text-sm text-gray-500 text-center mb-8 max-w-xs">
        You've been invited to join this company. Accept to get started.
      </p>

      {error && (
        <div className="w-full p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm mb-4 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      <div className="flex flex-col gap-3 w-full">
        <button
          onClick={handleAccept}
          disabled={accepting}
          className="w-full rounded-xl bg-brand px-4 py-3 text-sm font-semibold text-gray-900 transition hover:bg-brand-hover focus:outline-none focus:ring-4 focus:ring-brand/30 disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {accepting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Accepting...
            </>
          ) : (
            'Accept Invitation'
          )}
        </button>
        <button
          onClick={handleDecline}
          disabled={accepting}
          className="w-full rounded-xl bg-gray-100 px-4 py-3 text-sm font-semibold text-gray-700 transition hover:bg-gray-200 focus:outline-none focus:ring-4 focus:ring-gray-200 disabled:opacity-50"
        >
          Decline
        </button>
      </div>
    </div>
  );

  // Determine which content to render
  const renderContent = () => {
    if (loading) return renderLoading();
    if (error && !inviteDetails) return renderError();
    if (accepted) return renderSuccess();
    if (inviteDetails?.isExpired) return renderExpired();
    if (inviteDetails?.invitationStatus === 'accepted') return renderAlreadyAccepted();
    if (inviteDetails?.matchesCurrentUser === false) return renderWrongUser();
    if (inviteDetails) return renderNormal();
    return renderError();
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleDecline}>
        {/* Backdrop with blur */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-xl bg-white shadow-2xl transition-all w-full max-w-md">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-brand/10 rounded-lg">
                      <Building2 className="w-5 h-5 text-brand" />
                    </div>
                    <Dialog.Title className="text-base font-semibold text-gray-900">
                      Company Invitation
                    </Dialog.Title>
                  </div>
                  <button
                    onClick={handleDecline}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Body */}
                <div className="px-6">
                  {renderContent()}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

export default CompanyInviteModal;
